import { Dispatch, SetStateAction, useMemo } from 'react';
import { useQuoteStatuses } from '@axo/shared/data-access/hooks';
import { ace_index, loan_quote } from '@axo/shared/data-access/types';
import { FilterSet } from '../FilterSet';
import { MultiFilterSelect } from '../MultiFilterSelect';
import { setPageNumber } from '../SearchResults';

interface Props {
  query: ace_index.FindInput;
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>;
  editable?: boolean;
}

export function LoanQuoteStatusFilterSet({
  query,
  setQuery,
  editable = false,
}: Props) {
  const statuses = useQuoteStatuses();
  const groups = useMemo(() => mapStatuses(statuses.data), [statuses]);

  return (
    <FilterSet>
      {groups.map((group) => {
        const selectedCodes = statusesInRange(query, group.range);
        const values = group.children.filter((status) =>
          selectedCodes.includes(status.Code)
        );

        return (
          <MultiFilterSelect<Status>
            key={group.range}
            items={group.children}
            onSelect={(option) => setQuery(addStatus(option))}
            onRemove={(_, i) => setQuery(removeStatus(values[i]))}
            stringify={(option) => option.key}
            values={values}
            label={`${group.range}`}
            disabled={!editable}
          />
        );
      })}
    </FilterSet>
  );
}

function addStatus(status: Status): SetStateAction<ace_index.FindInput> {
  return (query) => {
    const statuses = new Set(query.LoanQuoteStatuses);

    if (statuses.has(status.Code)) {
      statuses.delete(status.Code);
    } else {
      statuses.add(status.Code);
    }

    const next = Array.from(statuses.values());

    return {
      ...setPageNumber(1)(query),
      LoanQuoteStatuses: next.length > 0 ? next : undefined,
    };
  };
}

function removeStatus(status: Status): SetStateAction<ace_index.FindInput> {
  return (query) => {
    if (!query.LoanQuoteStatuses) {
      return query;
    }

    const next = query.LoanQuoteStatuses.filter((code) => code !== status.Code);

    return {
      ...setPageNumber(1)(query),
      LoanQuoteStatuses: next.length > 0 ? next : undefined,
    };
  };
}

function statusesInRange(query: ace_index.FindInput, range: number): number[] {
  if (!query.LoanQuoteStatuses) {
    return [];
  }
  return query.LoanQuoteStatuses.filter((code) => statusInRange(code, range));
}

function statusInRange(status: number, range: number): boolean {
  return Math.floor(status / 100) * 100 === range;
}

// mapStatuses parses the status map returned from the api into
// some kind of actually workable object.
function mapStatuses(map?: loan_quote.QuoteStatuses) {
  if (!map) {
    return [];
  }

  // Add key as property to each status
  const statuses = Object.entries(map).map(([status, config]) => ({
    ...config,
    key: status,
  }));

  // Resolve top level status group (100, 200, etc)
  const groups = statuses.reduce((groups, status) => {
    const group = Math.floor(status.Code / 100) * 100;

    if (groups.includes(group)) {
      return groups;
    }

    return [...groups, group];
  }, [] as number[]);

  // Add children to each top level group
  return groups.map((group) => ({
    range: group,
    children: statuses.filter((status) => statusInRange(status.Code, group)),
  }));
}

type Status = loan_quote.QuoteStatuses[keyof loan_quote.QuoteStatuses] & {
  key: string;
};
